import type { Transition, Variants } from 'motion/react';

/** The shared Motion timing, read from the design tokens the document resolves right now: `--motion-base` and
 *  `--ease-out` in app/styles/tokens.css, as a skin retunes them (Studio runs at 150ms on its own curve) and
 *  as the reduced and off effects modes damp them (animations.css). Motion takes seconds and an easing, not
 *  custom properties, so `EffectsProvider` reads them once and again whenever the document changes skin or
 *  effects mode. The reveal distance is not read at all: the variants below name the token and the browser
 *  resolves whatever length a skin writes. */
export interface MotionTokens {
  transition: Transition;
  /** The shared row-entrance beat, in seconds. A surface that reveals several sections in sequence steps
   *  them by THIS rather than by a literal it picked itself, so a design that retunes the beat retimes every
   *  staggered entrance in the app at once. */
  stagger: number;
}

/** The value the document computes for a token, with any `var()` it refers to already substituted. An
 *  undeclared token is a broken stylesheet, not a design choice. */
function token(style: CSSStyleDeclaration, name: string): string {
  const value = style.getPropertyValue(name).trim();
  if (!value) throw new Error(`design token ${name} is not declared on the document`);
  return value;
}

/** A `time` as the skin contract admits it (src/shared/skins.ts isTime): a signed number in `s` or `ms`, in
 *  either case. CSS rejects a negative duration for a transition and runs none, and so does Motion. */
function seconds(name: string, value: string): number {
  const match = /^(-?\d*\.?\d+)(ms|s)$/i.exec(value);
  if (!match) throw new Error(`design token ${name} is not a time: ${value}`);
  return Math.max(0, Number(match[1]) / (match[2]!.toLowerCase() === 'ms' ? 1000 : 1));
}

/** `--ease-out` is an `opaque` token, which a custom skin cannot override (src/shared/skins.ts), so only this
 *  repository's stylesheets write it, and they write a cubic-bezier(). Anything else is a stylesheet bug. */
function cubicBezier(name: string, value: string): [number, number, number, number] {
  const match = /^cubic-bezier\(([^)]*)\)$/.exec(value);
  const points = match?.[1]!.split(',').map((point) => Number(point.trim()));
  if (!points || points.length !== 4 || points.some((point) => !Number.isFinite(point))) {
    throw new Error(`design token ${name} is not a cubic-bezier(): ${value}`);
  }
  return points as [number, number, number, number];
}

export function readMotionTokens(root: Element): MotionTokens {
  const style = getComputedStyle(root);
  return {
    transition: {
      duration: seconds('--motion-base', token(style, '--motion-base')),
      ease: cubicBezier('--ease-out', token(style, '--ease-out')),
    },
    stagger: seconds('--motion-stagger', token(style, '--motion-stagger')),
  };
}

/** The section entrance. Both offsets name the token itself, so the server and the first client render draw
 *  the same frame and the browser resolves any length a skin writes (`10px`, `1rem`, `clamp(…)`); the exit
 *  drifts by 40% of it. */
export const revealVariants = {
  hidden: { opacity: 0, y: 'var(--motion-distance-md)' },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 'calc(var(--motion-distance-md) * 0.4)' },
} satisfies Variants;
